import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  readonly key: string | null;
  readonly direction: SortDirection;
}

export interface UseSortedRowsResult<T> {
  readonly sorted: readonly T[];
  readonly sortKey: string | null;
  readonly sortDirection: SortDirection;
  readonly onSort: (key: string) => void;
}

function compareValues(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  const strA = typeof a === 'string' ? a : typeof a === 'number' ? String(a) : '';
  const strB = typeof b === 'string' ? b : typeof b === 'number' ? String(b) : '';

  const numA = parseDecimalForSort(strA);
  const numB = parseDecimalForSort(strB);

  if (numA !== null && numB !== null) {
    if (numA < numB) return -1;
    if (numA > numB) return 1;
    return 0;
  }

  return strA.localeCompare(strB, undefined, { sensitivity: 'base' });
}

function parseDecimalForSort(value: string): bigint | null {
  const cleaned = value.replace(/[$,%\s]/g, '').replace(/,/g, '');
  if (cleaned === '' || cleaned === '—') return null;

  const negative = cleaned.startsWith('-');
  const unsigned = negative ? cleaned.slice(1) : cleaned;
  const match = unsigned.match(/^(\d+)(?:\.(\d+))?$/);
  if (match === null) return null;

  const whole = match[1] ?? '0';
  const fraction = (match[2] ?? '').padEnd(4, '0').slice(0, 4);
  const magnitude = BigInt(whole + fraction);
  return negative ? -magnitude : magnitude;
}

export function useSortedRows<T>(
  rows: readonly T[],
  accessor: (row: T, key: string) => unknown,
): UseSortedRowsResult<T> {
  const [sort, setSort] = useState<SortState>({ key: null, direction: null });

  const onSort = useCallback((key: string) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: null };
    });
  }, []);

  const sorted = useMemo(() => {
    if (sort.key === null || sort.direction === null) return rows;

    const key = sort.key;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => dir * compareValues(accessor(a, key), accessor(b, key)));
  }, [rows, sort.key, sort.direction, accessor]);

  return { sorted, sortKey: sort.key, sortDirection: sort.direction, onSort };
}
